import { timestampByUTC } from '@repo-level/core-front';
import type { ThirdPartySummary } from 'api/cms/thirdPartySummaries';
import type { JackpotLevel, JackpotWin } from 'api/common';
import legendLevel from 'assets/images/jackpot/levels/legends.png';
import legendLevelIcon from 'assets/images/jackpot/levels/legendsIcon.png';
import pioneerLevel from 'assets/images/jackpot/levels/pioneers.png';
import pioneerLevelIcon from 'assets/images/jackpot/levels/pioneersIcon.png';
import titanLevel from 'assets/images/jackpot/levels/titans.png';
import titanLevelIcon from 'assets/images/jackpot/levels/titansIcon.png';
import { CurrencyCode } from 'helpers/currency';
import { Maybe } from 'monet';
import { findAvailableModal } from './thirdPartySummaries';

// — Pool levels ——————————————————————————————————————————————————————————————

export enum PoolLevelEnum {
  PIONEERS = 'level_3',
  LEGENDS = 'level_2',
  TITANS = 'level_1',
}

export interface IHitRange {
  from: number;
  to: number;
}

export interface IStaticPool {
  title: string;
  minBet: number; // USD
  image: string;
  type: PoolLevelEnum;
  hitRange: IHitRange; // USD
}

export interface IPool extends IStaticPool {
  prize: number;
}

export const LEVELS_CONFIG: Record<string, { title: string; icon: string }> = {
  [PoolLevelEnum.PIONEERS]: {
    title: 'shortTypes.pioneers',
    icon: pioneerLevelIcon,
  },
  [PoolLevelEnum.LEGENDS]: {
    title: 'shortTypes.legends',
    icon: legendLevelIcon,
  },
  [PoolLevelEnum.TITANS]: { title: 'shortTypes.titans', icon: titanLevelIcon },
};

export const POOLS_CONFIG: IStaticPool[] = [
  {
    title: 'jackpot:types.pioneers',
    type: PoolLevelEnum.PIONEERS,
    image: pioneerLevel,
    hitRange: { from: 70, to: 100 },
    minBet: 1,
  },
  {
    title: 'jackpot:types.legends',
    type: PoolLevelEnum.LEGENDS,
    image: legendLevel,
    hitRange: { from: 700, to: 1000 },
    minBet: 5,
  },
  {
    title: 'jackpot:types.titans',
    type: PoolLevelEnum.TITANS,
    image: titanLevel,
    hitRange: { from: 7000, to: 10_000 },
    minBet: 10,
  },
];

// — Prize utilities ——————————————————————————————————————————————————————————

export const CENTS_DIVIDER = 100;

export const updatePoolsWithPrizes = (levels: JackpotLevel[]): IPool[] =>
  POOLS_CONFIG.reduce<IPool[]>((pools, poolConfig) => {
    const level = levels.find((lvl) => lvl.identifier === poolConfig.type);

    if (level) {
      pools.push({ ...poolConfig, prize: level.amountCents / CENTS_DIVIDER });
    }

    return pools;
  }, []);

export const getTotalSumWithCurrency = (levels: JackpotLevel[]): number =>
  levels.reduce((sum, { amountCents }) => sum + amountCents, 0) / CENTS_DIVIDER;

// — Pool status ——————————————————————————————————————————————————————————————

export enum PoolStatusEnum {
  IN_HIT_ZONE = 'inHitZone',
  WIN_ANY_MOMENT = 'winAnyMoment',
  ABOUT_TO_BLOW = 'aboutToBlow',
}

export const POOL_STATUS_THRESHOLDS: Record<
  PoolLevelEnum,
  Partial<Record<PoolStatusEnum, number>>
> = {
  [PoolLevelEnum.PIONEERS]: {
    [PoolStatusEnum.ABOUT_TO_BLOW]: 90,
    [PoolStatusEnum.WIN_ANY_MOMENT]: 80,
    [PoolStatusEnum.IN_HIT_ZONE]: 70,
  },
  [PoolLevelEnum.LEGENDS]: {
    [PoolStatusEnum.ABOUT_TO_BLOW]: 900,
    [PoolStatusEnum.WIN_ANY_MOMENT]: 800,
    [PoolStatusEnum.IN_HIT_ZONE]: 700,
  },
  [PoolLevelEnum.TITANS]: {
    [PoolStatusEnum.WIN_ANY_MOMENT]: 9100,
    [PoolStatusEnum.IN_HIT_ZONE]: 7000,
  },
};

const STATUS_PRIORITY = [
  PoolStatusEnum.ABOUT_TO_BLOW,
  PoolStatusEnum.WIN_ANY_MOMENT,
  PoolStatusEnum.IN_HIT_ZONE,
] as const;

export const getPoolStatus = (
  poolType: PoolLevelEnum,
  prize: number
): PoolStatusEnum | null => {
  const thresholds = POOL_STATUS_THRESHOLDS[poolType];

  return (
    STATUS_PRIORITY.find((status) => {
      const threshold = thresholds[status];
      return threshold !== undefined && prize >= threshold;
    }) ?? null
  );
};

// — Stakes ———————————————————————————————————————————————————————————————————

export const AVAILABLE_STAKES: Partial<Record<CurrencyCode, number[]>> = {
  [CurrencyCode.USD]: [
    0.1, 0.2, 0.3, 0.4, 0.5, 0.7, 1, 1.25, 2, 2.5, 3, 3.5, 5, 7.5, 10, 12.5, 15,
    20, 25, 30, 40, 50, 75,
  ],
  [CurrencyCode.EUR]: [
    0.1, 0.2, 0.3, 0.4, 0.5, 0.7, 1, 1.25, 2, 2.5, 3, 3.5, 4, 5, 7.5, 10, 12.5,
    15, 20, 25, 30, 40, 50, 75,
  ],
  [CurrencyCode.CAD]: [
    0.2, 0.25, 0.3, 0.5, 0.7, 1, 1.25, 2, 2.5, 3, 4, 5, 6, 7.5, 8, 10, 12.5, 20,
    25, 30, 35, 50, 75,
  ],
  [CurrencyCode.AUD]: [
    0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 1, 1.4, 1.6, 2, 2.4, 2.8, 3, 3.2, 3.6, 4, 5,
    6, 7.5, 8, 10, 12, 15, 16, 20, 24, 25, 28, 30, 35, 40, 50, 60, 80,
  ],
  [CurrencyCode.NOK]: [
    1, 1.1, 2, 3, 5, 6, 7, 7.5, 8, 9, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55,
    75, 100, 110, 125, 150, 200, 250, 275, 300, 500, 600, 750,
  ],
  [CurrencyCode.NZD]: [
    0.2, 0.3, 0.5, 0.7, 1, 1.5, 2, 2.5, 3, 4, 5, 6, 7, 7.5, 8, 9, 10, 12.5, 15,
    20, 25, 30, 35, 40, 45, 50, 60, 75,
  ],
};

// Falls back to the highest available stake if amount exceeds all entries
export const findNearestStake = (
  amount: number,
  currency: CurrencyCode
): number => {
  const stakes = AVAILABLE_STAKES[currency];
  if (!stakes?.length) return amount;
  return stakes.find((stake) => stake >= amount) ?? stakes[stakes.length - 1];
};

// — Jackpot win / modal ——————————————————————————————————————————————————————

export const nickEncrypting = (nick: string) => {
  const chartCount = nick.length >= 5 ? 3 : 1;
  return `${nick.slice(0, chartCount)}***`;
};

const formatModalId = (createdAt: string) =>
  `JACKPOT_WIN_${timestampByUTC(createdAt)}`;

export const getAvailableJackpotModal = (
  userId: number | undefined,
  thirdPartySummaries: ThirdPartySummary[] | undefined,
  jackpotWins: JackpotWin[] | undefined
) => {
  if (!(userId && jackpotWins?.length)) return;

  const ids = jackpotWins.map(({ createdAt }) => formatModalId(createdAt));
  const modalId = Maybe.fromUndefined(thirdPartySummaries)
    .flatMap(findAvailableModal(ids))
    .orUndefined();

  return {
    modalId,
    modalData: modalId ? jackpotWins[ids.indexOf(modalId)] : undefined,
  };
};
